import * as React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Paper, Stack, Divider, alpha, Grid } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

type ViewType = "profit" | "turnover";

interface ApiItem {
    region?: string;
    posTurnoverActual?: number | null;
    posTurnoverTarget?: number | null;
    profit?: number | null;
    profitGoal?: number | null;
}

interface DashboardData {
    listOfData?: ApiItem[];
}

const CustomTooltip = (props: any) => {
    const { dataIndex, dataset, view } = props;
    if (dataIndex === undefined || !dataset) return null;

    const row = dataset[dataIndex];
    if (!row) return null;

    const isTurnover = view === "turnover";
    const actual = isTurnover ? row.turnoverActual : row.profitActual;
    const target = isTurnover ? row.turnoverTarget : row.profitTarget;
    const percentage = target > 0 ? ((actual / target) * 100).toFixed(1) : "0";

    return (
        <Paper elevation={8} sx={{ p: 2, minWidth: 240, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                {row.region} Bölgesi
            </Typography>
            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                        {isTurnover ? "Hedef Ciro:" : "Hedef Kar:"}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        {target.toLocaleString('tr-TR')} ₺
                    </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                        {isTurnover ? "Gerçekleşen Ciro:" : "Gerçekleşen Kar:"}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        {actual.toLocaleString('tr-TR')} ₺
                    </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight="bold">Başarı Oranı:</Typography>
                    <Box sx={{
                        bgcolor: parseFloat(percentage) >= 100 ? 'success.main' : 'primary.main',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                    }}>
                        %{percentage}
                    </Box>
                </Stack>
            </Stack>
        </Paper>
    );
};

export function TerritoryBarChart({
    dashboardData,
    view,
    setView
}: {
    dashboardData: DashboardData;
    view: ViewType;
    setView: (view: ViewType) => void;
}) {
    const list = dashboardData?.listOfData ?? [];

    const groupedData = React.useMemo(() => {
        const map: Record<string, { turnover: number; turnoverGoal: number; profit: number; profitGoal: number }> = {};

        list.forEach((item) => {
            const region = item.region ?? "Unknown";
            if (!map[region]) {
                map[region] = { turnover: 0, turnoverGoal: 0, profit: 0, profitGoal: 0 };
            }
            map[region].turnover += item.posTurnoverActual ?? 0;
            map[region].turnoverGoal += item.posTurnoverTarget ?? 0;
            map[region].profit += item.profit ?? 0;
            map[region].profitGoal += item.profitGoal ?? 0;
        });

        const tempData = Object.entries(map).map(([region, values]) => {
            const actual = view === "turnover" ? values.turnover : values.profit;
            const target = view === "turnover" ? values.turnoverGoal : values.profitGoal;

            return {
                region,
                turnoverActual: values.turnover,
                turnoverTarget: values.turnoverGoal,
                profitActual: values.profit,
                profitTarget: values.profitGoal,
                displayActual: actual,
                displayTarget: target,
                percentage: target > 0 ? Math.round((actual / target) * 100) : 0
            };
        }).sort((a, b) => b.displayActual - a.displayActual);

        return tempData;
    }, [list, view]);

    const handleChange = (
        _: React.MouseEvent<HTMLElement>,
        newValue: ViewType | null
    ) => {
        if (newValue) setView(newValue);
    };

    return (
        <Box sx={{ width: "100%", textAlign: "center" }}>
            <Grid container alignContent={'space-evenly'} sx={{ mb: 3 }}>
                <Grid item md={4} alignContent={"center"}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        Bölgesel Performans
                    </Typography>
                </Grid>

                <Grid item md={8} textAlign={'end'}>
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        onChange={handleChange}
                        size="small"
                    >
                        <ToggleButton value="profit">POS Karı</ToggleButton>
                        <ToggleButton value="turnover">Ciro</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#4F46E5', borderRadius: '50%' }} />
                    <Typography variant="caption" fontWeight={600}>Gerçekleşen</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#E2E8F0', borderRadius: '50%' }} />
                    <Typography variant="caption" fontWeight={600}>Hedef</Typography>
                </Stack>
            </Box>

            <BarChart
                dataset={groupedData}
                yAxis={[
                    {
                        scaleType: 'band',
                        dataKey: 'region',
                        valueFormatter: (value) => {
                            const item = groupedData.find(d => d.region === value);
                            return `${value}\n(%${item?.percentage ?? 0})`;
                        },
                    },
                ]}
                xAxis={[{}]}
                series={[
                    {
                        dataKey: 'displayActual',
                        label: 'Gerçekleşen',
                        color: '#4F46E5',
                    },
                    {
                        dataKey: 'displayTarget',
                        label: 'Hedef',
                        color: '#E2E8F0',
                    }
                ]}
                layout="horizontal"
                height={350}
                margin={{ top: 20, bottom: 50, left: 80, right: 10 }}
                slots={{ tooltip: (props) => <CustomTooltip {...props} view={view} /> }}
                slotProps={{
                    legend: { hidden: true }
                }}
                sx={{
                    '& .MuiBarElement-root': {
                        rx: 4,
                    },
                    '& .MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                        fontSize: 12,
                        fontWeight: 600,
                    },
                    '& .MuiChartsAxis-tickLabel': {
                        whiteSpace: 'pre-line',
                        textAlign: 'center',
                    },
                }}
            />
        </Box>
    );
}
